import { useState, useEffect, useRef, useCallback } from 'react';
import { RESEND_COOLDOWN } from '../constants';

const ONE_HOUR_MS = 3600 * 1000;
const TIMER_INTERVAL_MS = 1000;

export const useOTPState = () => {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [otpRequestCount, setOtpRequestCount] = useState<number>(0);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const resetIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback((initialTime: number) => {
    clearTimer();
    setResendTimer(initialTime);
    setIsResendDisabled(true);

    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        const newTime = prev - 1;
        if (newTime <= 0) {
          setIsResendDisabled(false);
          clearTimer();
          return 0;
        }
        return newTime;
      });
    }, TIMER_INTERVAL_MS);
  }, [clearTimer]);

  useEffect(() => {
    const storedCooldown = localStorage.getItem('resendCooldown');
    const storedRequestCount = localStorage.getItem('otpRequestCount');

    if (storedRequestCount) {
      setOtpRequestCount(parseInt(storedRequestCount, 10));
    }

    const cooldownTimestamp = storedCooldown ? parseInt(storedCooldown, 10) : 0;
    const elapsedTime = (Date.now() - cooldownTimestamp) / 1000;
    const remainingTime = Math.max(0, RESEND_COOLDOWN - elapsedTime);

    if (remainingTime > 0) {
      startTimer(Math.ceil(remainingTime));
    } else {
      localStorage.setItem('resendCooldown', Date.now().toString());
      startTimer(RESEND_COOLDOWN);
    }

    return () => {
      clearTimer();
    };
  }, [startTimer, clearTimer]);

  useEffect(() => {
    resetIntervalRef.current = setInterval(() => {
      setOtpRequestCount(0);
      localStorage.removeItem('otpRequestCount');
    }, ONE_HOUR_MS);

    return () => {
      if (resetIntervalRef.current) {
        clearInterval(resetIntervalRef.current);
      }
    };
  }, []);

  const setResendTimerWithStart = useCallback((time: number) => {
    startTimer(time);
  }, [startTimer]);

  return {
    otp,
    setOtp,
    isResendDisabled,
    setIsResendDisabled,
    resendTimer,
    setResendTimer: setResendTimerWithStart,
    otpRequestCount,
    setOtpRequestCount,
  };
};
